import { AppState, UiFilterState, UiState } from '../../models/app-state.model';
import { Card } from '../../models/card.model';
import { Category } from '../../models/category.model';
import { SCHEMA_VERSION } from '../../constants/app.constants';
import {
  CurrentPersistedAppState,
  PersistedAppState,
} from '../schemas/persisted-app-state.model';
import {
  PersistedCard,
  PersistedCategory,
  PersistedUiFilterState,
  PersistedUiState,
} from '../schemas/persisted-entities.model';
import { PersistedAppStateV2 } from '../schemas/persisted-app-state-v2.model';
import { PersistedAppStateV1 } from '../schemas/persisted-app-state-v1.model';
import { preserveLegacyPersistedAppState } from '../legacy/persisted-legacy-preservation';

export function isPersistedAppStateV1(candidate: unknown): candidate is PersistedAppStateV1 {
  return hasPersistedAppStateShape(candidate) && candidate.schemaVersion === 1;
}

export function isPersistedAppStateV2(candidate: unknown): candidate is PersistedAppStateV2 {
  return hasPersistedAppStateShape(candidate) && candidate.schemaVersion === 2;
}

function hasPersistedAppStateShape(candidate: unknown): candidate is Record<string, unknown> & { schemaVersion: number } {
  if (candidate === null || typeof candidate !== 'object') {
    return false;
  }

  const value = candidate as Record<string, unknown>;
  const categories = value['categories'];
  const cards = value['cards'];

  return typeof value['schemaVersion'] === 'number'
    && typeof value['savedAt'] === 'string'
    && Array.isArray(categories)
    && categories.every((entry) => entry !== null && typeof entry === 'object')
    && Array.isArray(cards)
    && cards.every((entry) => entry !== null && typeof entry === 'object')
    && value['ladder'] !== null
    && typeof value['ladder'] === 'object'
    && Array.isArray((value['ladder'] as Record<string, unknown>)['levels'])
    && value['ui'] !== null
    && typeof value['ui'] === 'object';
}

export function parsePersistedAppState(candidate: unknown): PersistedAppState | null {
  if (isPersistedAppStateV1(candidate)) {
    return candidate;
  }

  if (isPersistedAppStateV2(candidate)) {
    return candidate;
  }

  return null;
}

export function mapPersistedAppStateToRuntime(persistedState: PersistedAppState): AppState {
  const stateWithLegacy = preserveLegacyPersistedAppState(persistedState);

  return {
    schemaVersion: SCHEMA_VERSION,
    savedAt: stateWithLegacy.savedAt,
    categories: stateWithLegacy.categories.map(mapPersistedCategoryToRuntime),
    ladder: {
      levels: [...stateWithLegacy.ladder.levels],
      ...(stateWithLegacy.ladder.legacy ? { legacy: structuredClone(stateWithLegacy.ladder.legacy) } : {}),
    },
    cards: stateWithLegacy.cards.map(mapPersistedCardToRuntime),
    ui: mapPersistedUiStateToRuntime(stateWithLegacy.ui),
    ...(stateWithLegacy.legacy ? { legacy: structuredClone(stateWithLegacy.legacy) } : {}),
  };
}

export function mapRuntimeAppStateToPersisted(state: AppState): CurrentPersistedAppState {
  return {
    schemaVersion: 2,
    savedAt: state.savedAt,
    categories: state.categories.map(mapRuntimeCategoryToPersisted),
    ladder: {
      levels: [...state.ladder.levels],
      ...(state.ladder.legacy ? { legacy: structuredClone(state.ladder.legacy) } : {}),
    },
    cards: state.cards.map(mapRuntimeCardToPersisted),
    ui: mapRuntimeUiStateToPersisted(state.ui),
    ...(state.legacy ? { legacy: structuredClone(state.legacy) } : {}),
  };
}

function mapPersistedCategoryToRuntime(category: PersistedCategory): Category {
  return {
    id: category.id,
    name: category.name,
    color: category.color,
    isSystem: category.isSystem,
    ...(category.legacy ? { legacy: structuredClone(category.legacy) } : {}),
  };
}

function mapRuntimeCategoryToPersisted(category: Category): PersistedCategory {
  return {
    id: category.id,
    name: category.name,
    color: category.color,
    isSystem: category.isSystem,
    ...(category.legacy ? { legacy: structuredClone(category.legacy) } : {}),
  };
}

function mapPersistedCardToRuntime(card: PersistedCard): Card {
  return {
    id: card.id,
    title: card.title,
    description: card.description,
    notes: card.notes,
    categoryId: card.categoryId,
    status: card.status,
    showInLadder: card.showInLadder,
    priority: card.priority,
    importance: card.importance,
    parentCardId: card.parentCardId,
    createdAt: card.createdAt,
    updatedAt: card.updatedAt,
    deadline: card.deadline,
    manualProgress: card.manualProgress,
    ladderGroupColor: card.ladderGroupColor ?? null,
    ...(card.legacy ? { legacy: structuredClone(card.legacy) } : {}),
  };
}

function mapRuntimeCardToPersisted(card: Card): PersistedCard {
  return {
    id: card.id,
    title: card.title,
    description: card.description,
    notes: card.notes,
    categoryId: card.categoryId,
    status: card.status,
    showInLadder: card.showInLadder,
    priority: card.priority,
    importance: card.importance,
    parentCardId: card.parentCardId,
    createdAt: card.createdAt,
    updatedAt: card.updatedAt,
    deadline: card.deadline,
    manualProgress: card.manualProgress,
    ladderGroupColor: card.ladderGroupColor ?? null,
    ...(card.legacy ? { legacy: structuredClone(card.legacy) } : {}),
  };
}

function mapPersistedUiStateToRuntime(uiState: PersistedUiState): UiState {
  return {
    ladder: mapPersistedUiFilterToRuntime(uiState.ladder),
    allCards: mapPersistedUiFilterToRuntime(uiState.allCards),
    ...(uiState.legacy ? { legacy: structuredClone(uiState.legacy) } : {}),
  };
}

function mapRuntimeUiStateToPersisted(uiState: UiState): PersistedUiState {
  return {
    ladder: mapRuntimeUiFilterToPersisted(uiState.ladder),
    allCards: mapRuntimeUiFilterToPersisted(uiState.allCards),
    ...(uiState.legacy ? { legacy: structuredClone(uiState.legacy) } : {}),
  };
}

function mapPersistedUiFilterToRuntime(filter: PersistedUiFilterState): UiFilterState {
  return {
    textQuery: filter.textQuery,
    status: filter.status,
    categoryId: filter.categoryId,
    quickFilter: filter.quickFilter,
    ...(filter.legacy ? { legacy: structuredClone(filter.legacy) } : {}),
  };
}

function mapRuntimeUiFilterToPersisted(filter: UiFilterState): PersistedUiFilterState {
  return {
    textQuery: filter.textQuery,
    status: filter.status,
    categoryId: filter.categoryId,
    quickFilter: filter.quickFilter,
    ...(filter.legacy ? { legacy: structuredClone(filter.legacy) } : {}),
  };
}